import {
  YARD_STATE_DENIALS,
  type YardAccessDenial,
  type YardStateDenial,
} from "@etudescarre/constants";
import { isNil, isNotNil } from "@etudescarre/utils";
import { formatDay } from "@/lib/utils/date";

// Shown in a toast, never on a page of its own.
export const YARD_ACCESS_DENIED_TITLE = "Chantier inaccessible";

const DENIAL_MESSAGES: Record<YardAccessDenial, string> = {
  yard_not_commissioned:
    "Ce chantier n'est pas encore mis en service : il s'ouvrira à sa mise en service.",
  yard_closed: "Ce chantier est clôturé : il n'est plus accessible.",
  yard_off:
    "Ce chantier est interrompu (OFF) : son accès est suspendu jusqu'à sa remise en service.",
  account_inactive:
    "Votre compte est désactivé : contactez votre correspondant ChantierCarré.",
  not_on_yard: "Vous n'êtes rattaché à aucun contrat actif de ce chantier.",
};

const STAFF_NOT_ON_YARD =
  "Votre compte ChantierCarré n'est rattaché à aucun contrat de ce chantier. Pour le voir comme l'un de ses utilisateurs, incarnez-le depuis l'accueil staff.";

const SELECTION_ANTICIPATIONS: Record<YardStateDenial, string> = {
  yard_not_commissioned:
    "Anticipation : ses chantiers en préparation s'ouvrent avant leur mise en service.",
  yard_off:
    "Anticipation : ses chantiers interrompus (OFF) s'ouvrent pendant l'incarnation, alors que leur accès est suspendu.",
  yard_closed:
    "Anticipation : ses chantiers clôturés s'ouvrent pendant l'incarnation, alors qu'ils ne lui sont plus accessibles.",
};

const YARD_NOT_FOUND_MESSAGE = "Ce chantier n'existe pas.";

const YARD_LOAD_FAILED_MESSAGE =
  "Le chantier n'a pas pu être chargé. Réessayez dans un instant.";

const isYardAccessDenial = (code: string): code is YardAccessDenial =>
  Object.hasOwn(DENIAL_MESSAGES, code);

/** Why this account cannot open the yard, on every screen that says so. */
export function yardAccessMessage(
  denial: YardAccessDenial,
  closedOn?: string | null,
): string {
  if (denial === "yard_closed" && isNotNil(closedOn)) {
    return `Ce chantier a été clôturé le ${formatDay(closedOn)} : il n'est plus accessible.`;
  }
  return DENIAL_MESSAGES[denial];
}

export type AnticipatedYard = {
  yardId: number;
  denial: YardStateDenial;
  closedOn: string | null;
  listed: boolean;
};

/**
 * What the impersonation banner says about yards closed to the user. In a yard,
 * the state that closes this one, with the reason the user gets; elsewhere, one
 * line per state among the yards their selection lists.
 */
export function yardAnticipations(
  yards: AnticipatedYard[],
  currentYardId: string | undefined,
): string[] {
  if (isNil(currentYardId)) {
    return YARD_STATE_DENIALS.filter((denial) =>
      yards.some((yard) => yard.listed && yard.denial === denial),
    ).map((denial) => SELECTION_ANTICIPATIONS[denial]);
  }
  const current = yards.find((yard) => String(yard.yardId) === currentYardId);
  if (isNil(current)) return [];
  return [
    `Anticipation : ce compte ne peut pas ouvrir ce chantier lui-même. ${yardAccessMessage(current.denial, current.closedOn)}`,
  ];
}

/**
 * Why the yard context request failed, from its HTTP status and error code.
 * `staff` is a ChantierCarré account using its own session (not impersonating).
 */
export function yardOpenFailureMessage(
  status: number,
  failure: { error?: string; closedOn?: string } | undefined,
  staff: boolean,
): string {
  const code = failure?.error;
  if (status === 404) return YARD_NOT_FOUND_MESSAGE;
  if (code === "not_on_yard" && staff) return STAFF_NOT_ON_YARD;
  if (isNotNil(code) && isYardAccessDenial(code)) {
    return yardAccessMessage(code, failure?.closedOn);
  }
  return YARD_LOAD_FAILED_MESSAGE;
}
